'use client';

import React, { forwardRef, useState, useRef, useEffect } from 'react';
import classnames from 'classnames';

import Badge, { BadgeProps } from '../Badge/Badge';
import Box from '../Box/Box';
import Typography from '../Typography/Typography';
import RadioButton from './components/RadioButton';

import styles from './RadioGroup.module.scss';

// TODO: Import proper types when focusTrap utils are converted
// import { handleKeyDown } from "./utils/focusTrap";

// Temporary placeholder for handleKeyDown
const handleKeyDown = (
  _event: KeyboardEvent,
  _index: number,
  _radioGroupRef: React.RefObject<HTMLUListElement | null>,
  _radioRefs: React.MutableRefObject<(HTMLLIElement | null)[]>
) => {
  // TODO: Implement proper keyboard navigation
};

type Theme = 'dark' | 'light';
type Direction = 'ltr' | 'rtl';
type ValidationType = 'success' | 'warning' | 'error';

type ValidationResponse = {
  validationType: ValidationType;
  message: string;
};

type RadioOption = {
  value: string;
  label: string;
  description?: string;
  default?: boolean;
  disabled?: boolean;
};

type RegisterFunction = {
  (name: string): {
    onChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
    onBlur: (event: React.FocusEvent<HTMLInputElement>) => void;
    name: string;
    ref: React.Ref<HTMLInputElement>;
  };
};

export type RadioGroupProps = React.HTMLAttributes<HTMLUListElement> & {
  name?: string; // for use with react-hook-form
  register?: RegisterFunction; // for use with react-hook-form
  theme?: Theme;
  /** Label text displayed above the radio group */
  label?: string;
  /** Helper text displayed below the label */
  helperText?: string;
  options: RadioOption[];
  /** Currently selected value (controlled mode) */
  selectedValue?: string;
  direction?: Direction;
  isDisabled?: boolean;
  /** Whether the field is required */
  required?: boolean;
  handleChange?: (option: RadioOption) => void;
  dataTestId?: string;
  gaComponent?: string;
  logEvents?: boolean;
  /** Layout direction for the radio buttons */
  layout?: 'vertical' | 'horizontal';
  /**
   * Validation response object containing error/warning/success state and message.
   */
  validationResponse?: ValidationResponse;
  /**
   * Whether to show the validation message area.
   * @default true
   */
  showValidation?: boolean;
  /**
   * Callback when radio group loses focus
   */
  onBlur?: () => void;
  /** Optional badge displayed next to the label */
  badge?: BadgeProps;
};

const RadioGroup = forwardRef<HTMLDivElement, RadioGroupProps>(
  (
    {
      name, //for use with react-hook-form
      register, //for use with react-hook-form
      theme = 'dark',
      label,
      helperText,
      options,
      selectedValue,
      direction = 'ltr',
      isDisabled,
      required = false,
      handleChange = () => {},
      dataTestId,
      gaComponent,
      logEvents = true,
      layout = 'vertical',
      validationResponse,
      showValidation = true,
      onBlur,
      badge,
      ...props
    },
    ref
  ) => {
    // Determine initial index from selectedValue or default option
    const getActiveIndex = () => {
      if (selectedValue !== undefined) {
        const idx = options?.findIndex((option) => option.value === selectedValue);
        return idx !== undefined && idx >= 0 ? idx : -1;
      }
      return options?.findIndex((option) => option.default) ?? -1;
    };

    const [activeIndex, setActiveIndex] = useState(() => getActiveIndex());

    // Sync activeIndex when selectedValue changes (controlled mode)
    useEffect(() => {
      if (selectedValue !== undefined) {
        const idx = options?.findIndex((option) => option.value === selectedValue);
        setActiveIndex(idx !== undefined && idx >= 0 ? idx : -1);
      }
    }, [selectedValue, options]);

    const keyDownHandlerRef = useRef<((event: KeyboardEvent) => void) | null>(null);
    const radioGroupRef = useRef<HTMLUListElement>(null);
    const radioRefs = useRef<(HTMLLIElement | null)[]>([]);

    const handleSelection = (option: RadioOption, index: number) => {
      if (index === activeIndex) return;
      setActiveIndex(index);
      handleChange(option);
      radioRefs.current.forEach((radio) => {
        const focusTarget = radio?.querySelector('.radioButtonFocusTarget') as HTMLElement;
        focusTarget?.blur();
      });
    };

    const handleGroupFocus = (event: React.FocusEvent<HTMLUListElement>) => {
      event.preventDefault();
      // If the radio group is focused, focus the selected radio button
      if (event.currentTarget === event.target) {
        if (radioRefs.current[activeIndex]) {
          const focusTarget = radioRefs.current[activeIndex]?.querySelector(
            '.radioButtonFocusTarget'
          ) as HTMLElement;
          focusTarget?.focus();
        } else {
          // If no radio button is selected, click & focus the first radio button with .radioButtonFocusTarget class (if any)
          const firstRadio = radioRefs.current.find((ref) =>
            ref?.querySelector('.radioButtonFocusTarget')
          );
          if (firstRadio) {
            const focusTarget = firstRadio.querySelector('.radioButtonFocusTarget') as HTMLElement;
            // Only move focus here - do NOT click. Clicking would select the first option
            // (e.g. "Air") purely as a side effect of the group receiving focus, even though
            // the user hasn't made a choice yet.
            focusTarget?.focus();
          }
        }
      }
    };

    // Add keyboard behaviors to the radio button when focused
    const handleButtonFocus = (e: React.FocusEvent<HTMLDivElement>, index: number) => {
      keyDownHandlerRef.current = (event: KeyboardEvent) =>
        handleKeyDown(event, index, radioGroupRef, radioRefs);
      e.currentTarget.addEventListener('keydown', keyDownHandlerRef.current);
    };

    // Remove keydown behaviors when radio button is blurred
    const handleButtonBlur = (e: React.FocusEvent<HTMLDivElement>) => {
      if (keyDownHandlerRef.current) {
        e.currentTarget.removeEventListener('keydown', keyDownHandlerRef.current);
        keyDownHandlerRef.current = null;
      }
      // Check if focus is leaving the RadioGroup entirely
      const relatedTarget = e.relatedTarget as HTMLElement | null;
      if (!relatedTarget || !radioGroupRef.current?.contains(relatedTarget)) {
        onBlur?.();
      }
    };

    return (
      <Box className={classnames(styles.radioGroup)} ref={ref}>
        {/* Hidden input for react-hook-form registration */}
        {name && (
          <input
            type="hidden"
            value={selectedValue ?? options?.find((o) => o.default)?.value ?? ''}
            {...(register ? register(name) : { name })}
            aria-describedby={`${name}-validation-message`}
          />
        )}
        {(label || helperText) && (
          <Box mb={8}>
            {label && (
              <Box className={styles.labelRow}>
                <Typography
                  className={styles.label}
                  variant="label"
                  size="small"
                  direction={direction}
                  theme={theme}
                  color="gray-3"
                >
                  {label}
                  {required && '*'}
                </Typography>
                {badge && <Badge {...badge} theme={theme} />}
              </Box>
            )}
            {helperText && (
              <Typography
                className={styles.helperText}
                variant="body"
                size="extraSmall"
                direction={direction}
                theme={theme}
                color="gray-5"
              >
                {helperText}
              </Typography>
            )}
          </Box>
        )}
        <ul
          className={classnames(styles.radioButtonList, styles[layout])}
          tabIndex={0}
          onFocus={handleGroupFocus}
          ref={radioGroupRef}
          {...props}
        >
          {options.map((option, index) => (
            <li
              key={index}
              ref={(el) => {
                radioRefs.current[index] = el;
              }}
            >
              <RadioButton
                theme={theme}
                index={index}
                name={name}
                option={option}
                groupCount={options.length}
                direction={direction}
                isChecked={index === activeIndex}
                isDisabled={option.disabled || isDisabled}
                onFocus={(e) => handleButtonFocus(e, index)}
                onBlur={handleButtonBlur}
                onClick={() => handleSelection(option, index)}
                dataTestId={`${dataTestId}_option_${index}`}
                gaComponent={gaComponent}
                logEvents={logEvents}
              />
            </li>
          ))}
        </ul>
        {showValidation && (
          <Box className={styles.validationMessageWrapper} role="alert" aria-live="assertive">
            {validationResponse?.message && (
              <Typography
                className={styles.validationMessage}
                variant="body"
                size="extraSmall"
                direction={direction}
                theme={theme}
                color={
                  validationResponse.validationType === 'error'
                    ? 'error-1'
                    : validationResponse.validationType === 'warning'
                      ? 'warning-1'
                      : 'success-1'
                }
                id={`${name}-validation-message`}
              >
                {validationResponse.message}
              </Typography>
            )}
          </Box>
        )}
      </Box>
    );
  }
);

RadioGroup.displayName = 'RadioGroup';

export default RadioGroup;
